import Shepherd from 'shepherd.js';
import { TOUR_BUTTONS } from '../shepherd-defaults';
import {
  clearShepherdStepDismissedByUser,
  clearShepherdStepTooltipPersistenceSuppress,
  dismissCurrentShepherdStep,
  hideCurrentShepherdStepForTourTransition,
  isShepherdTourStepSpotlightEligible,
  registerInteractiveTourGuards,
  suppressShepherdStepTooltipPersistence,
  SHEPHERD_INTERACTIVE_STEP_OPTIONS,
  type ShepherdStep,
} from '../shepherd-interactive-step';
import {
  cancelPostActivityModalScrollAfterShow,
  clearPostActivityLocationTourUi,
  POST_ACTIVITY_LOCATION_STEP_ID,
  resetPostActivityLocationTargetPadding,
  resolvePostActivityLocationTarget,
  scrollPostActivityModalToStepAfterShow,
  setPostActivityLocationSpotlightActive,
} from '../post-activity-location.shepherd';
import {
  registerTourHighlightedFieldLockGuards,
  SHEPHERD_TOUR_READONLY_HIGHLIGHT_STEP_OPTIONS,
  withTourFieldLockTipMessage,
} from '../shepherd-tour-field-lock';
import { delay, waitForElement } from '../shepherd.utils';

export { POST_ACTIVITY_LOCATION_STEP_ID } from '../post-activity-location.shepherd';

type ShepherdTour = InstanceType<typeof Shepherd.Tour>;

export const POST_ACTIVITY_TOUR = 'post-activity';

export const POST_ACTIVITY_TYPE_STEP_ID = 'post-activity-type';

/**
 * Resolves CSS prefix so the tour targets either the create-activity modal or the full-page form,
 * without attaching to a hidden duplicate when both exist on `/add-activity`.
 */
export function resolvePostActivitySelectorPrefix(): string {
  const toggle = document.getElementById('create-golf-event-toggle') as HTMLInputElement | null;
  const path = typeof window !== 'undefined' ? window.location.pathname.split('?')[0] : '';
  const onAddActivityPage = path.includes('add-activity');
  const modalOpen = !!toggle?.checked;
  if (modalOpen && document.querySelector('.create-golf-event-modal')) {
    return '.create-golf-event-modal ';
  }
  if (onAddActivityPage) {
    return '.activity-post ';
  }
  return '.create-golf-event-modal ';
}

function getCurrentStepId(tour: ShepherdTour): string | undefined {
  return tour.getCurrentStep?.()?.id ?? (tour as { currentStep?: { id?: string } }).currentStep?.id;
}

function isShepherdActiveTour(tour: ShepherdTour): boolean {
  return typeof Shepherd !== 'undefined' && Shepherd.activeTour === tour;
}

/** Advance tour off Activity Type only if that step is still showing and the tour is active. */
function safeAdvanceFromActivityTypeStep(tour: ShepherdTour): void {
  if (!isShepherdActiveTour(tour)) {
    return;
  }
  if (getCurrentStepId(tour) !== POST_ACTIVITY_TYPE_STEP_ID) {
    return;
  }
  void tour.next();
}

/** Dismiss type-step tip and advance to location when the form moves forward (OK or modal Next). */
export function advancePostActivityTourFromActivityTypeStep(tour: ShepherdTour): void {
  if (!isShepherdActiveTour(tour) || getCurrentStepId(tour) !== POST_ACTIVITY_TYPE_STEP_ID) {
    return;
  }
  dismissCurrentShepherdStep(tour);
  scheduleAdvanceFromActivityTypeStep(tour);
}

/** After Activity Type OK — advance when the location field is on screen (modal or single-page). */
function scheduleAdvanceFromActivityTypeStep(tour: ShepherdTour): void {
  const toggle = document.getElementById('create-golf-event-toggle') as HTMLInputElement | null;
  const modalOpen = !!toggle?.checked;
  if (!modalOpen) {
    void delay(600).then(() => safeAdvanceFromActivityTypeStep(tour));
    return;
  }
  void waitForElement(
    `.create-golf-event-modal [data-tour="${POST_ACTIVITY_LOCATION_STEP_ID}"]`,
    600000,
  ).then((el) => {
    if (el) {
      safeAdvanceFromActivityTypeStep(tour);
    }
  });
}

function restorePostActivityTypeStepForBack(tour: ShepherdTour): void {
  const typeStep = tour.getById?.(POST_ACTIVITY_TYPE_STEP_ID) as ShepherdStep | undefined;
  clearShepherdStepDismissedByUser(typeStep);
}

function syncPostActivityLocationTourSpotlight(tour: ShepherdTour): void {
  if (isShepherdTourStepSpotlightEligible(tour, POST_ACTIVITY_LOCATION_STEP_ID)) {
    setPostActivityLocationSpotlightActive(true);
    return;
  }
  setPostActivityLocationSpotlightActive(false);
}

function beginPostActivityLocationStepBackNavigation(tour: ShepherdTour): void {
  cancelPostActivityModalScrollAfterShow();
  clearPostActivityLocationTourUi();
  const locationStep = tour.getById?.(POST_ACTIVITY_LOCATION_STEP_ID) as ShepherdStep | undefined;
  suppressShepherdStepTooltipPersistence(locationStep);
  if (getCurrentStepId(tour) === POST_ACTIVITY_LOCATION_STEP_ID) {
    hideCurrentShepherdStepForTourTransition(tour, POST_ACTIVITY_TYPE_STEP_ID);
  }
}

function showPostActivityTypeTourStep(tour: ShepherdTour): void {
  if (!isShepherdActiveTour(tour)) {
    return;
  }
  restorePostActivityTypeStepForBack(tour);
  void tour.back();
}

const POST_ACTIVITY_LOCATION_BACK = {
  text: '← Back',
  classes: 'shepherd-button-secondary',
  action(this: ShepherdTour) {
    const tour = this;
    beginPostActivityLocationStepBackNavigation(tour);
    restorePostActivityTypeStepForBack(tour);

    const toggle = document.getElementById('create-golf-event-toggle') as HTMLInputElement | null;
    if (toggle?.checked) {
      const formBack = document.querySelector(
        '.create-golf-event-modal .event-details-footer button.event-btn-back',
      ) as HTMLButtonElement | null;
      if (formBack) {
        formBack.click();
        void waitForElement(
          '.create-golf-event-modal [data-tour="post-activity-activity-type"]',
          3000,
        ).then(() => showPostActivityTypeTourStep(tour));
        return;
      }
    }
    showPostActivityTypeTourStep(tour);
  },
};

/**
 * Post / create activity form: Activity type → Location → Skill level → Create Activity.
 * Step 1: OK hides only this step (tour continues; later steps still run).
 * The tour advances to Location when Activity Details is shown (modal) or after a short delay on the single-page form.
 */
export function buildPostActivityTour(tour: ShepherdTour): void {
  registerInteractiveTourGuards(tour);
  registerTourHighlightedFieldLockGuards(
    tour,
    [POST_ACTIVITY_LOCATION_STEP_ID],
    resolvePostActivityLocationTarget,
  );
  const prefix = resolvePostActivitySelectorPrefix();

  const postActivityTypeOk: {
    text: string;
    classes: string;
    action(this: ShepherdTour): void;
  } = {
    text: 'OK',
    classes: 'shepherd-button-primary',
    action(this: ShepherdTour) {
      advancePostActivityTourFromActivityTypeStep(this);
    },
  };

  tour.on('hide', (ev: { step?: { id?: string } }) => {
    if (ev?.step?.id === POST_ACTIVITY_LOCATION_STEP_ID) {
      clearPostActivityLocationTourUi();
    }
  });

  tour.on('show', (ev: { step?: { id?: string } }) => {
    const stepId = ev?.step?.id;
    if (stepId) {
      scrollPostActivityModalToStepAfterShow(stepId);
    }
    if (stepId === POST_ACTIVITY_LOCATION_STEP_ID) {
      resetPostActivityLocationTargetPadding();
      syncPostActivityLocationTourSpotlight(tour);
      queueMicrotask(() => syncPostActivityLocationTourSpotlight(tour));
      requestAnimationFrame(() => syncPostActivityLocationTourSpotlight(tour));
    } else if (stepId) {
      clearPostActivityLocationTourUi();
      const locationStep = tour.getById?.(POST_ACTIVITY_LOCATION_STEP_ID) as ShepherdStep | undefined;
      clearShepherdStepTooltipPersistenceSuppress(locationStep);
    }
  });

  tour.addStep({
    id: POST_ACTIVITY_TYPE_STEP_ID,
    title: 'Tip',
    text: 'Choose the type of golf experience you want to organize.',
    attachTo: { element: `${prefix}[data-tour="post-activity-activity-type"]`, on: 'bottom' },
    buttons: [TOUR_BUTTONS.skip, postActivityTypeOk],
    canClickTarget: true,
    beforeShowPromise() {
      return waitForElement(`${prefix}[data-tour="post-activity-activity-type"]`, 8000).then(
        (el) => {
          if (el) {
            scrollPostActivityModalToStepAfterShow(POST_ACTIVITY_TYPE_STEP_ID);
          }
          return undefined;
        },
      );
    },
  });

  tour.addStep({
    id: POST_ACTIVITY_LOCATION_STEP_ID,
    title: 'Tip',
    text: withTourFieldLockTipMessage('Search courses by name or city.'),
    attachTo: { element: `${prefix}[data-tour="${POST_ACTIVITY_LOCATION_STEP_ID}"]`, on: 'bottom' },
    buttons: [POST_ACTIVITY_LOCATION_BACK, TOUR_BUTTONS.next],
    ...SHEPHERD_TOUR_READONLY_HIGHLIGHT_STEP_OPTIONS,
    modalOverlayOpeningPadding: 12,
    beforeShowPromise() {
      return waitForElement(`${prefix}[data-tour="${POST_ACTIVITY_LOCATION_STEP_ID}"]`, 15000).then(
        (el) => {
          if (el) {
            scrollPostActivityModalToStepAfterShow(POST_ACTIVITY_LOCATION_STEP_ID);
          }
          return undefined;
        },
      );
    },
  });

  tour.addStep({
    id: 'post-activity-skill',
    title: 'Tip',
    text: 'Set golfer preferences so the right players join.',
    attachTo: { element: `${prefix}[data-tour="post-activity-skill-level"]`, on: 'top' },
    buttons: [TOUR_BUTTONS.back, TOUR_BUTTONS.next],
    ...SHEPHERD_INTERACTIVE_STEP_OPTIONS,
    modalOverlayOpeningPadding: 12,
    beforeShowPromise() {
      return waitForElement(`${prefix}[data-tour="post-activity-skill-level"]`, 15000).then(
        (el) => {
          if (el) {
            scrollPostActivityModalToStepAfterShow('post-activity-skill');
          }
          return undefined;
        },
      );
    },
  });

  tour.addStep({
    id: 'post-activity-create',
    title: 'Tip',
    text: 'Once created, nearby golfers can request to join.',
    attachTo: { element: `${prefix}[data-tour="post-activity-create-button"]`, on: 'top' },
    buttons: [TOUR_BUTTONS.back, TOUR_BUTTONS.finish],
    ...SHEPHERD_INTERACTIVE_STEP_OPTIONS,
    beforeShowPromise() {
      return waitForElement(`${prefix}[data-tour="post-activity-create-button"]`, 15000).then(
        (el) => {
          if (el) {
            scrollPostActivityModalToStepAfterShow('post-activity-create');
          }
          return undefined;
        },
      );
    },
  });
}
